/**
 * Lesson video call surface: remote video large, self-view in the corner,
 * mute + camera toggles underneath, remote mute/camera badges on top.
 */

import { useEffect, useRef } from 'react';
import { t } from '../lib/i18n.ts';
import type { VideoCall } from '../lib/rtc.ts';
import { Button, ErrorText, cls } from '../lib/ui.tsx';

function Video({ stream, muted, mirror, className }: { stream: MediaStream | null; muted?: boolean; mirror?: boolean; className?: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current && ref.current.srcObject !== stream) ref.current.srcObject = stream;
  }, [stream]);
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted={muted}
      className={cls(mirror && '-scale-x-100', className)}
    />
  );
}

export default function VideoCallPanel({
  call,
  otherLabel,
  waitingText,
  extraControls,
}: {
  call: VideoCall;
  otherLabel: string;
  waitingText: string;
  extraControls?: React.ReactNode;
}) {
  const remoteVisible = !!call.remoteStream && !call.remoteState?.videoOff;
  return (
    <div className="space-y-2">
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-slate-900">
        <Video stream={call.remoteStream} className="h-full w-full object-cover" />
        {!remoteVisible && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="px-6 text-center text-sm text-slate-300">
              {call.remoteStream
                ? `📷 ${otherLabel} — ${t('call.cameraOff', 'camera is off')}`
                : waitingText}
            </p>
          </div>
        )}
        {call.remoteState?.muted && (
          <span className="absolute top-2 left-2 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white">
            🔇 {otherLabel} {t('call.isMuted', 'is muted')}
          </span>
        )}
        {/* self view */}
        <div className="absolute right-2 bottom-2 w-24 overflow-hidden rounded-lg border border-white/30 bg-black/60 sm:w-32">
          {call.videoOff || !call.hasCamera ? (
            <div className="flex aspect-[4/3] items-center justify-center text-lg">📷🚫</div>
          ) : (
            <Video stream={call.localStream} muted mirror className="aspect-[4/3] w-full object-cover" />
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={call.muted ? 'primary' : 'secondary'}
          className="px-3 py-1.5 text-xs"
          onClick={call.toggleMute}
        >
          {call.muted ? `🔇 ${t('call.unmute', 'Unmute')}` : `🎙 ${t('call.mute', 'Mute')}`}
        </Button>
        <Button
          variant={call.videoOff ? 'primary' : 'secondary'}
          className="px-3 py-1.5 text-xs"
          onClick={call.toggleVideo}
          disabled={!call.hasCamera}
        >
          {call.videoOff ? `📷 ${t('call.videoOn', 'Camera on')}` : `📷 ${t('call.videoOff', 'Camera off')}`}
        </Button>
        {extraControls}
        <span className="ml-auto text-xs text-slate-400">
          {call.connectionState === 'connected'
            ? `🟢 ${t('call.connected', 'call connected')}`
            : call.remoteStream
              ? call.connectionState
              : ''}
        </span>
      </div>
      <ErrorText error={call.mediaError} />
    </div>
  );
}
